import { getFrontendConfigValue } from '@arnold/common';
import { FC, useEffect, useState } from 'react';
import { Loading } from '../../components';
import NotificationCard from '../../components/NotificationCard';
import auth from '../../lib/auth';

function getAllowedRedirectHosts(): Set<string> {
  const hosts = new Set<string>();
  for (const configKey of ['ARNOLD_URL', 'SELF_CARE_URL'] as const) {
    const configuredUrl = getFrontendConfigValue(configKey);
    if (!configuredUrl) {
      continue;
    }
    try {
      hosts.add(new URL(configuredUrl).hostname);
    } catch {
      // ignore invalid config URL
    }
  }
  return hosts;
}

function resolveSafeRedirectUri(rawRedirectUri?: string | null, redirectToTalk?: boolean): string {
  const fallback = '/';

  if (!rawRedirectUri) {
    if (redirectToTalk) {
      return `${getFrontendConfigValue('ARNOLD_URL')}/overview`;
    }
    return fallback;
  }

  const trimmed = rawRedirectUri.trim();
  if (!trimmed) {
    return fallback;
  }

  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  try {
    const redirectUrl = new URL(trimmed);
    if (getAllowedRedirectHosts().has(redirectUrl.hostname)) {
      return redirectUrl.toString();
    }
  } catch {
    // Ignore invalid URL and fall back to overview
  }

  return fallback;
}

/**
 * Landing page for the OIDC provider redirect. Forwards code+state to the
 * API (which holds the state, nonce and PKCE verifier), stores the client
 * side session cookies and redirects to the original destination.
 */
const SsoCallbackScreen: FC = () => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const exchangeCode = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      if (!code || !state) {
        setError('Missing code or state from SSO response');
        return;
      }

      try {
        const response = await fetch(getFrontendConfigValue('API_URL') + '/auth/sso/callback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
          const body = await response.json();
          setError(body.message || 'SSO login failed');
          return;
        }

        const { id, intercomHash, ssoSession, redirectToTalk, redirectAfterLogin } = await response.json();
        auth.setUserId(id.toString());
        auth.setIntercomHash(intercomHash);
        auth.setSsoSession(!!ssoSession);

        window.location.href = resolveSafeRedirectUri(redirectAfterLogin, redirectToTalk);
      } catch (e) {
        setError('SSO login failed. Please try again.');
      }
    };

    exchangeCode();
  }, []);

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: 32 }}>
        <NotificationCard isError header="SSO Login Failed" content={error} />
      </div>
    );
  }

  return <Loading />;
};

export default SsoCallbackScreen;
